import { defineStore } from 'pinia'

// Shared geometry of the right sidebar. The width belongs to the sidebar as a
// whole — every tab (pages/outline/properties/history/resources) uses it.
//
// `rightPanelWidth` is the TOTAL width of the sidebar: resize handle + content
// + tab strip. It is the user's *preferred* width; what actually gets rendered
// is getEffectiveRightPanelWidth(), which additionally clamps against the space
// the window currently has, so the tab strip can never end up off-screen. The
// preferred width is what is persisted, so shrinking the window never destroys
// the chosen width.
export const RIGHT_PANEL_MIN_WIDTH = 260
export const RIGHT_PANEL_MAX_WIDTH = 700
export const RIGHT_PANEL_DEFAULT_WIDTH = 380
export const RIGHT_PANEL_TAB_STRIP_WIDTH = 28
// Drag handle on the panel's left edge, and the narrowest content area still
// worth rendering: below that the panel shows its tab strip alone rather than
// letting handle + strip overflow the space it was given.
export const RIGHT_PANEL_HANDLE_WIDTH = 6
export const RIGHT_PANEL_MIN_CONTENT_WIDTH = 120
// Space the designer workspace keeps for itself before the sidebar has to give
// way — the center column shrinks first, but not to nothing.
export const MIN_CENTER_WIDTH = 320

const RIGHT_PANEL_WIDTH_KEY = 'haspDesigner.rightPanelWidth'

export function clampRightPanelWidth(width) {
  const value = Number(width)
  if (!Number.isFinite(value)) return RIGHT_PANEL_DEFAULT_WIDTH
  return Math.min(RIGHT_PANEL_MAX_WIDTH, Math.max(RIGHT_PANEL_MIN_WIDTH, Math.round(value)))
}

/**
 * The width to actually render: the preferred width, capped by what is left
 * over after the left sidebar, a usable center workspace and the tab strip.
 * Pure and integer-only — DOM widths derived from fractional measurements can
 * oscillate between two layouts, so everything is floored here.
 *
 * Collapsed renders the tab strip alone. When the window is too small for even
 * the minimum, the tab strip still wins: it is never partially hidden.
 */
export function getEffectiveRightPanelWidth({
  preferredWidth = RIGHT_PANEL_DEFAULT_WIDTH,
  viewportWidth = 0,
  leftWidth = 0,
  tabStripWidth = RIGHT_PANEL_TAB_STRIP_WIDTH,
  minCenterWidth = MIN_CENTER_WIDTH,
  collapsed = false,
} = {}) {
  const strip = Math.max(0, Math.floor(tabStripWidth))
  if (collapsed) return strip
  const preferred = clampRightPanelWidth(preferredWidth)
  // No measurement yet (first render): trust the preferred width.
  if (!Number.isFinite(viewportWidth) || viewportWidth <= 0) return preferred
  const available = Math.floor(viewportWidth - leftWidth - minCenterWidth)
  // Not enough room for handle + a usable content area: show the tab strip
  // alone, so it stays whole instead of being pushed out by its own siblings.
  if (available < strip + RIGHT_PANEL_HANDLE_WIDTH + RIGHT_PANEL_MIN_CONTENT_WIDTH) return strip
  return Math.max(strip, Math.min(preferred, available))
}

function loadStoredWidth() {
  try {
    const stored = localStorage.getItem(RIGHT_PANEL_WIDTH_KEY)
    if (stored === null) return RIGHT_PANEL_DEFAULT_WIDTH
    const value = Number(stored)
    return Number.isFinite(value) ? clampRightPanelWidth(value) : RIGHT_PANEL_DEFAULT_WIDTH
  } catch {
    return RIGHT_PANEL_DEFAULT_WIDTH
  }
}

export const useUiLayoutStore = defineStore('uiLayout', {
  state: () => ({
    rightPanelWidth: loadStoredWidth(),
    rightPanelCollapsed: false,
    activeRightTab: 'pages',
  }),
  actions: {
    // Tab-strip click: clicking the active tab collapses, any other tab opens
    // that tab at the shared width.
    selectRightTab(tab) {
      if (!this.rightPanelCollapsed && this.activeRightTab === tab) {
        this.rightPanelCollapsed = true
        return
      }
      this.activeRightTab = tab
      this.rightPanelCollapsed = false
    },
    // Programmatic tab switch (selection, navigation). Never changes the
    // collapsed state — the user stays collapsed if they chose to be.
    showRightTab(tab) {
      this.activeRightTab = tab
    },
    // Explicit "take me to this panel" navigation (e.g. widget → dataset):
    // switches the tab and expands the sidebar at the shared width.
    revealRightTab(tab) {
      this.activeRightTab = tab
      this.rightPanelCollapsed = false
    },
    setRightPanelWidth(width) {
      const clamped = clampRightPanelWidth(width)
      this.rightPanelWidth = clamped
      // Only a real, non-collapsed width is persisted; collapsing never
      // overwrites it.
      try {
        localStorage.setItem(RIGHT_PANEL_WIDTH_KEY, String(clamped))
      } catch {
        /* storage unavailable — keep the in-memory width */
      }
    },
  },
})
